import { Building2, Info, Loader2 } from 'lucide-react';
import React, { useState } from 'react';

import { useOnboardingStore } from '../../../store/onboarding.store';
import { saveCompanyData } from '../../../utils/saveCompanyData';

interface BusinessDetailsStepProps {
  onNext: () => void;
}

export function BusinessDetailsStep({ onNext }: BusinessDetailsStepProps) {
  const { companyData, setCompanyData, companyId, setCompanyId } = useOnboardingStore();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!companyData.name.trim()) return;

    setSaving(true);
    setError(null);

    try {
      // Save incrementally - UPDATE if companyId exists, CREATE if not
      const result = await saveCompanyData(companyId, {
        name: companyData.name,
        slogan: companyData.slogan || null,
        ein: companyData.ein || null,
      });

      // Store companyId if it was just created
      if (result.created && result.companyId) {
        setCompanyId(result.companyId);
      }

      onNext();
    } catch (err) {
      console.error('[BusinessDetailsStep] Save error:', err);
      setError(err instanceof Error ? err.message : 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-full bg-[#9B1421]/20 flex items-center justify-center mx-auto mb-4">
          <Building2 size={32} className="text-rollops-accent" />
        </div>
        <h2 className="text-2xl font-bold text-[#EBFFEE]">Business Entity Information</h2>
        <p className="text-[#A9A3A3] mt-2">Primary business entity details for your company</p>
      </div>

      {/* Business Name */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-[#EBFFEE]">
          Business Name <span className="text-rollops-accent">*</span>
        </label>
        <input
          type="text"
          value={companyData.name || ''}
          onChange={(e) => setCompanyData({ name: e.target.value })}
          placeholder="e.g., Elite MMA Academy LLC"
          className="w-full px-4 py-3 rounded-lg bg-[#504A4A] text-[#EBFFEE] placeholder:text-[#A9A3A3] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50 transition-all"
          required
          maxLength={100}
        />
        <p className="text-xs text-[#A9A3A3]">Legal business entity name (may differ from gym location names)</p>
      </div>

      {/* Slogan */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-[#EBFFEE]">
          Slogan / Tagline
        </label>
        <input
          type="text"
          value={companyData.slogan || ''}
          onChange={(e) => setCompanyData({ slogan: e.target.value })}
          placeholder="e.g., Train like a champion"
          className="w-full px-4 py-3 rounded-lg bg-[#504A4A] text-[#EBFFEE] placeholder:text-[#A9A3A3] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50 transition-all"
          maxLength={255}
        />
        <p className="text-xs text-[#A9A3A3]">Optional - A short description that represents your gym</p>
      </div>

      {/* EIN */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-[#EBFFEE]">
          EIN (Employer Identification Number)
        </label>
        <input
          type="text"
          value={companyData.ein || ''}
          onChange={(e) => {
            // Format as XX-XXXXXXX
            const value = e.target.value.replace(/\D/g, '');
            if (value.length <= 9) {
              const formatted = value.length > 2 
                ? `${value.slice(0, 2)}-${value.slice(2)}` 
                : value;
              setCompanyData({ ein: formatted });
            }
          }}
          placeholder="XX-XXXXXXX"
          className="w-full px-4 py-3 rounded-lg bg-[#504A4A] text-[#EBFFEE] placeholder:text-[#A9A3A3] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50 transition-all"
          maxLength={10}
        />
        <div className="flex items-start gap-2 text-xs text-[#A9A3A3]">
          <Info size={14} className="mt-0.5 flex-shrink-0" />
          <span>Optional - Required for tax reporting. You can add this later.</span>
        </div>
      </div>

      {error && (
        <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-3">
          <p className="text-red-400 text-sm text-center">{error}</p>
        </div>
      )}

      {/* Continue Button */}
      <button
        type="submit"
        disabled={!companyData.name.trim() || saving}
        className="w-full py-3 px-4 bg-[#9B1421] hover:bg-[#7A0F1A] disabled:bg-[#504A4A] disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
      >
        {saving ? (
          <>
            <Loader2 size={18} className="animate-spin" />
            Saving...
          </>
        ) : (
          'Continue'
        )}
      </button>
    </form>
  );
}

